import type { ReactNode } from 'react';

import type { SwitchProps } from './Switch';

/**
 * Maps the bare MUI `Switch` props onto the Design System 2.0 API.
 *
 * TODO(HNS-264): temporary. This mapper exists only so the visual refresh can be gated inside the
 * wrapper instead of at its call sites; retiring the flag means deleting it and letting call sites
 * speak the DS vocabulary directly.
 *
 * Returning `null` means "the DS switch cannot express this one" and sends the caller back to MUI.
 *
 * The wrapper owns its optional visible label. Without one, the name comes from `aria-label` or
 * `aria-labelledby`. A switch that sits inside an external `FormControlLabel` must not reach this branch,
 * because the DS switch is itself a `<label>` and the two would nest — `ReactHookForm/Switch` and
 * `ChatModerationSwitch` therefore render the MUI switch directly, and own their own DS branch.
 *
 * Cases the DS switch cannot express, all of which stay on MUI:
 *
 *   - `size` other than `small`, INCLUDING an omitted one — mapped by measured height, as
 *     `ReactHookForm/Switch/toDsProps` records: MUI `small` is a 2px match, `medium` is not.
 *   - `sx` — an Emotion prop with no effect on a Tailwind control.
 *   - a non-string `value`, which the DS control cannot take.
 *   - any other key (`className`, `color`, `inputProps`, `slotProps`, `required`, `readOnly`, `data-*`, `edge`):
 *     the wrapper forwards only the handled set, so anything else would be dropped silently. The type
 *     names no such key, but a `data-*` attribute or a spread can still carry one at runtime.
 *   - no accessible name. A switch announced as just "switch" is a defect rather than a cosmetic
 *     gap, so the DS branch refuses to draw one (`AutoOptOutSwitch` used to be exactly that, and now
 *     passes `aria-label`).
 */
/** Mirrors the DS naming union while retaining explicit ARIA names alongside visible text. */
export type DsProps =
  | { label: ReactNode; 'aria-label'?: string; 'aria-labelledby'?: string }
  | { 'aria-labelledby': string }
  | { 'aria-label': string };

export const toDsProps = ({
  label,
  size,
  sx,
  value,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  // Forwarded by the wrapper, so not this mapper's business.
  'aria-describedby': _ariaDescribedBy,
  checked: _checked,
  defaultChecked: _defaultChecked,
  disabled: _disabled,
  id: _id,
  name: _name,
  onChange: _onChange,
  ...unhandled
}: SwitchProps): DsProps | null => {
  // Base UI derives aria-labelledby from a wrapping label, which would append its text to aria-label.
  // Keep MUI's explicit-name precedence for this combination.
  if (sx || size !== 'small' || (label !== undefined && ariaLabel && !ariaLabelledBy)) {
    return null;
  }

  if (Object.values(unhandled).some((prop) => prop !== undefined)) {
    return null;
  }

  // MUI types the input `value` as `unknown`; the DS control takes a string and a cast could let an object through.
  if (value !== undefined && typeof value !== 'string') {
    return null;
  }

  if (label !== undefined) {
    return { label, 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledBy };
  }

  if (ariaLabelledBy) {
    return { 'aria-labelledby': ariaLabelledBy };
  }

  if (ariaLabel) {
    return { 'aria-label': ariaLabel };
  }

  return null;
};
